import { FORM_ERROR } from 'final-form';
import React, { useEffect, useState } from 'react';
import Button from 'react-bootstrap/Button';
import ConfirmModal from '../common/dialogs/ConfirmModal';
import { formatPhoneNumber } from '../format/PhoneNumber';
import { EnabledDisabled } from '../models/enums';
import { Facility } from '@reliance/types/models/Facility';
import BaseHtmlTable from '../table/BaseHtmlTable';
import TextAlertAddModal from './TextMessageAlertAddModal';
import { IEventNotificationCondition, IEventNotificationTrigger } from './event-notification-factory';
import { Inmate } from '@reliance/types/models/Inmate';
import eventNotificationService from './event-notification-service';
import { useInmatesByFacilityId } from '../hooks/Inmate';
import { useNotifyTabLoading } from '../hooks/useTabLoadingReporter';

type TextMessageAlertsProps = {
  facility: Facility
}

const getInmateDetails = (inmateId: number|string, inmates: Inmate[]) =>
  inmates
    .filter(i => i.id == inmateId)
    .map(i => `${i.firstName} ${i.lastName} (${i.account})`)
    .pop();

const TextMessageAlerts = (props: TextMessageAlertsProps) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [loadingTriggers, setLoadingTriggers] = useState(true);
  const [triggers, setTriggers] = useState<IEventNotificationTrigger[]>([]);
  const [removeTrigger, setRemoveTrigger] = useState<IEventNotificationTrigger>(null);
  const { result: inmates, loading: loadingInmates } = useInmatesByFacilityId(props.facility.id);

  useNotifyTabLoading('text-message/alerts', loadingInmates || loadingTriggers);

  const loadTriggers = () => {
    setLoadingTriggers(true);
    eventNotificationService
      .getTextMessageAlertTriggers(props.facility.id)
      .then(setTriggers)
      .then(() => setLoadingTriggers(false));
  }

  useEffect(() => {
    loadTriggers();
  }, []);

  const columns = [
    { key: 'inmate', name: 'Inmate' },
    { key: 'outsideParty', name: 'Outside Party' },
    { key: 'phrase', name: 'Phrase' },
    { key: 'actions', name: 'Actions' },
    { key: 'options', name: 'Options' }
  ];

  // Massage the triggers into applicable/usable columns
  const rows = (triggers||[]).map(trigger => ({
    id: trigger.id,

    inmate: getInmateDetails((trigger.conditions.filter(c =>
        c.status === EnabledDisabled.Enabled &&
        c.metadataPath.indexOf('_inmateId') > -1
      ).pop() || {} as IEventNotificationCondition).value, inmates?.data ?? []) || <i>any</i>,

    outsideParty: formatPhoneNumber(
      (trigger.conditions.filter(c =>
        c.status === EnabledDisabled.Enabled &&
        c.metadataPath.indexOf('customerAni') > -1
      ).pop() || {} as IEventNotificationCondition).value
    ) || <i>any</i>,

    phrase: (trigger.conditions.filter(c =>
        c.status === EnabledDisabled.Enabled &&
        c.metadataPath.indexOf('message') > -1
      ).pop() || {} as IEventNotificationCondition).value || <i>any</i>,

    actions: trigger.actions.map(a =>
        <div key={`${trigger.id}-action-${a.id}`}>{String(a.actionType)} {a.destinationName} ({a.destination})</div>
      ),

    options: <div>
      <Button onClick={() => setRemoveTrigger(trigger)} size="sm" variant="outline-danger">Remove</Button>
    </div>
  }));

  const addAlert = (data) => {
    return eventNotificationService
      .createTextMessageAlertTrigger(data, props.facility)
      .then(response => {
        if (response && response.data && response.data.success) {
          setShowAddModal(false);
          loadTriggers();
          return null;
        }
        return { [FORM_ERROR]: Error('Unexpected error while creating new alert') };
      })
      .catch(error => ({ [FORM_ERROR]: error }));
  }

  if (loadingInmates || loadingTriggers) {
    return (
      <div className="jarviswidget jarviswidget-color-blueDark">
        <div className="row">
          <div className="col-md-12">
            <h3>Loading...</h3>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <button className="btn btn-primary" onClick={() => setShowAddModal(true)}>Add Alert</button>
      <br/><br/>

      <BaseHtmlTable
        columns={columns}
        rows={rows}
        perPage={1000}
        />

      {showAddModal &&
        <TextAlertAddModal
          inmates={inmates?.data ?? []}
          onClose={() => setShowAddModal(false)}
          onSubmit={addAlert}
          />
      }

      {removeTrigger &&
        <ConfirmModal
          show={true}
          title="Remove Text Message Alert"
          onAccept={() =>
            eventNotificationService
              .deleteTrigger(removeTrigger.id)
              .then(() => {
                setRemoveTrigger(null);
                loadTriggers();
              })
              .catch(() => setRemoveTrigger(null))
          }
          onClose={() => setRemoveTrigger(null)}
          acceptText="Remove">
          Are you sure you want to remove the text message alert?
        </ConfirmModal>
      }
    </>
  )
}

export default TextMessageAlerts;
